import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { distances, grid, border, colors } from '../../../styles/constants';
import { Dl, Dt, Dd } from '../../../components/DescriptionList/Horizontal';
import { H2 } from '../../../components/Typography';
import { Session, Transaction, TransactionEvent } from '../../../types/checkout';
import { formatExpirationDate, getProductName, usesPaymentType } from '../../transactionHelpers';
import Pan from '../../../components/Formatters/Pan';
import { Link } from 'react-router-dom';
import { useAccountId } from '../../../auth/useAccountId';
import { UnstyledLink } from '../../../components/StyledLink';
import Hr from '../../../components/Hr';
import { PaymentProductType } from '../../types';
import { DateTime } from '../../../components/Formatters';
export interface TransactionDataProps extends WithTranslation {
    transaction: Transaction;
    session?: Session;
}

const TransactionData = (
    {
        t,
        transaction,
        session,
    }: TransactionDataProps
) => {
    const accountId = useAccountId();

    let expirationDate = undefined;
    if (usesPaymentType(transaction, PaymentProductType.Klarna)) {
        const authEvents = transaction.payment_product === 'klarna'
            ? transaction.events.filter(
                (event: TransactionEvent) =>
                    event.event === 'AUTHORIZE' && event.success
            )
            : [];
        const newestAuthEvent = authEvents.length > 0 ? authEvents[authEvents.length - 1] : undefined;
        expirationDate = newestAuthEvent && newestAuthEvent.metadata && (newestAuthEvent.metadata as any)['klarna:authorization_expiration'];
    }

    return (
        <Wrapper>
            <H2>{t('payments.transaction.transaction_data')}</H2>
            <Hr />
            <Dl>
                <Dt doubleColumn>{t('payments.fields.id')}</Dt>
                <Dd>{transaction.id}</Dd>
            </Dl>
            <Dl>
                <Dt doubleColumn>{t('payments.fields.merchant_reference')}</Dt>
                <Dd>{transaction.merchant_reference}</Dd>
            </Dl>
            {transaction.merchant_reference_2 && (
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.merchant_reference_2')}</Dt>
                    <Dd>{transaction.merchant_reference_2}</Dd>
                </Dl>
            )}
            <Dl>
                <Dt doubleColumn>{t('payments.fields.type')}</Dt>
                <Dd>{t(getProductName(transaction))}</Dd>
            </Dl>
            {transaction.payment_operation && (
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.payment_operation')}</Dt>
                    <Dd>
                        {t(`payments.payment_operation.${transaction.payment_operation}`)}
                    </Dd>
                </Dl>
            )}
            {transaction.store?.id && (
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.store')}</Dt>
                    <Dd>{transaction.store.id}</Dd>
                </Dl>
            )}

            {transaction.dynamic_descriptor && (
                <>
                    <Spacer />
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.dynamic_descriptor')}</Dt>
                        <Dd>{transaction.dynamic_descriptor}</Dd>
                    </Dl>
                </>
            )}

            {transaction.payment_product === 'netaxept' && (
                <>
                    <Spacer />
                    {transaction.card && (
                        <>
                            {transaction.card.masked_pan && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card')}</Dt>
                                    <Dd>
                                        <Pan value={transaction.card.masked_pan} />
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.card.brand && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_brand')}</Dt>
                                    <Dd>{transaction.card.brand}</Dd>
                                </Dl>
                            )}
                            {transaction.card.expiry_date && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_expiry_date')}</Dt>
                                    <Dd>{transaction.card.expiry_date}</Dd>
                                </Dl>
                            )}
                            {transaction.card.type && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_type')}</Dt>
                                    <Dd>{transaction.card.type}</Dd>
                                </Dl>
                            )}
                            {transaction.card.issuing_bank && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_issuing_bank')}</Dt>
                                    <Dd>{transaction.card.issuing_bank}</Dd>
                                </Dl>
                            )}
                            {transaction.card.country && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_country')}</Dt>
                                    <Dd>{transaction.card.country}</Dd>
                                </Dl>
                            )}
                        </>
                    )}
                    <Spacer />
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.gateway')}</Dt>
                        <Dd>
                            {t([
                                `payments.payment_product.${transaction.payment_product}`,
                                transaction.payment_product,
                            ])}
                        </Dd>
                    </Dl>
                    {transaction.metadata && (
                        <>
                            {transaction.metadata['netaxept:merchant_id'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.netaxept.merchant_id')}</Dt>
                                    <Dd>{transaction.metadata['netaxept:merchant_id']}</Dd>
                                </Dl>
                            )}

                            {transaction.metadata['netaxept:transaction_id'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.netaxept.transaction_id')}</Dt>
                                    <Dd>{transaction.metadata['netaxept:transaction_id']}</Dd>
                                </Dl>
                            )}
                        </>
                    )}
                </>
            )}
            {transaction.payment_product === 'payex' && (
                <>
                    {transaction.card && (
                        <>
                            <Spacer />
                            {transaction.card.masked_pan && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card')}</Dt>
                                    <Dd>
                                        <Pan value={transaction.card.masked_pan} />
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.card.brand && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_brand')}</Dt>
                                    <Dd>{transaction.card.brand}</Dd>
                                </Dl>
                            )}
                            {transaction.card.expiry_date && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_expiry_date')}</Dt>
                                    <Dd>{transaction.card.expiry_date}</Dd>
                                </Dl>
                            )}
                            {transaction.card.type && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_type')}</Dt>
                                    <Dd>{transaction.card.type}</Dd>
                                </Dl>
                            )}
                            {transaction.card.issuing_bank && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_issuing_bank')}</Dt>
                                    <Dd>{transaction.card.issuing_bank}</Dd>
                                </Dl>
                            )}
                            {transaction.card.country && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_country')}</Dt>
                                    <Dd>{transaction.card.country}</Dd>
                                </Dl>
                            )}
                            {transaction.card.acquirer_transaction_type && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_acquirer_transaction_type')}</Dt>
                                    <Dd>{transaction.card.acquirer_transaction_type}</Dd>
                                </Dl>
                            )}
                            {transaction.card.acquirer_stan && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_acquirer_stan')}</Dt>
                                    <Dd>{transaction.card.acquirer_stan}</Dd>
                                </Dl>
                            )}
                        </>
                    )}
                    <Spacer />
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.gateway')}</Dt>
                        <Dd>
                            {t([
                                `payments.payment_product.${transaction.payment_product}`,
                                transaction.payment_product,
                            ])}
                        </Dd>
                    </Dl>
                    {transaction.metadata && (
                        <>
                            {transaction.metadata['payex:payment:id'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_id')}</Dt>
                                    <Dd>
                                        {transaction.metadata['payex:payment:id'].replace(
                                            '/psp/creditcard/payments/',
                                            ''
                                        ).replace(
                                            '/psp/vipps/payments/',
                                            ''
                                        )}
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.metadata['payex:payment:number'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_number')}</Dt>
                                    <Dd>{transaction.metadata['payex:payment:number']}</Dd>
                                </Dl>
                            )}
                            {transaction.metadata['payex:payment:payee_info:payee_name'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_payee_name')}</Dt>
                                    <Dd>
                                        {transaction.metadata['payex:payment:payee_info:payee_name']}
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.metadata['payex:payment:payee_info:payee_id'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_payee_id')}</Dt>
                                    <Dd>
                                        {transaction.metadata['payex:payment:payee_info:payee_id']}
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.metadata['payex:payment:payee_info:subsite'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_subsite')}</Dt>
                                    <Dd>
                                        {transaction.metadata['payex:payment:payee_info:subsite']}
                                    </Dd>
                                </Dl>
                            )}
                        </>
                    )}
                </>
            )}

            {transaction.payment_product === 'instabank' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['instabank:Sale.ExternalReference'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.instabank.external_reference')}</Dt>
                            <Dd title={transaction.metadata['instabank:Sale.ExternalReference']}>
                                {transaction.metadata['instabank:Sale.ExternalReference']}
                            </Dd>
                        </Dl>
                    )}
                    {transaction.metadata['instabank:Sale.Sequence'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.instabank.sequence')}</Dt>
                            <Dd>{transaction.metadata['instabank:Sale.Sequence']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['instabank:Sale.Product.Name'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.instabank.product')}</Dt>
                            <Dd>{transaction.metadata['instabank:Sale.Product.Name']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {transaction.payment_product === 'santander' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['santander:debit_account.ApplicationNumber'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.santander.applicationNumber')}</Dt>
                            <Dd>{transaction.metadata['santander:debit_account.ApplicationNumber']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {transaction.payment_product === 'bambora' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.card && (
                        <>
                            {transaction.card.masked_pan && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card')}</Dt>
                                    <Dd>
                                        <Pan value={transaction.card.masked_pan} />
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.card.brand && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_brand')}</Dt>
                                    <Dd>{transaction.card.brand}</Dd>
                                </Dl>
                            )}
                            {transaction.card.expiry_date && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_expiry_date')}</Dt>
                                    <Dd>{transaction.card.expiry_date}</Dd>
                                </Dl>
                            )}
                            {transaction.card.type && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.card_type')}</Dt>
                                    <Dd>{transaction.card.type}</Dd>
                                </Dl>
                            )}
                            {transaction.card.eci && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.eci')}</Dt>
                                    <Dd>{transaction.card.eci}</Dd>
                                </Dl>
                            )}
                        </>
                    )}
                    <Spacer />
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.gateway')}</Dt>
                        <Dd>
                            {t([
                                `payments.payment_product.${transaction.payment_product}`,
                                transaction.payment_product,
                            ])}
                        </Dd>
                    </Dl>
                    {transaction.metadata && (
                        <>
                            {transaction.metadata['gateway:id'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.payex.payment_id')}</Dt>
                                    <Dd>{transaction.metadata['gateway:id']}</Dd>
                                </Dl>
                            )}
                            {transaction.metadata['bambora:reference'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.bambora.reference')}</Dt>
                                    <Dd>
                                        {transaction.metadata['bambora:reference']}
                                    </Dd>
                                </Dl>
                            )}
                            {transaction.metadata['bambora:merchant_number'] && (
                                <Dl>
                                    <Dt doubleColumn>{t('payments.fields.bambora.merchant_number')}</Dt>
                                    <Dd>
                                        {transaction.metadata['bambora:merchant_number']}
                                    </Dd>
                                </Dl>
                            )}
                        </>
                    )}
                </>
            )}

            {transaction.payment_product === 'vipps' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['vipps:transaction.orderId'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.vipps.order_id')}</Dt>
                            <Dd>{transaction.metadata['vipps:transaction.orderId']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['vipps:merchantInfo.merchantSerialNumber'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.vipps.merchantSerialNumber')}</Dt>
                            <Dd>{transaction.metadata['vipps:merchantInfo.merchantSerialNumber']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {transaction.payment_product === 'santander' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['santander:debit_account.StatusCode'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.santander.statusCode')}</Dt>
                            <Dd>{transaction.metadata['santander:debit_account.StatusCode']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {transaction.payment_product === 'klarna' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['gateway:id'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.order_id')}</Dt>
                            <Dd>{transaction.metadata['gateway:id']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['merchant_name'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.merchant_name')}</Dt>
                            <Dd>{transaction.metadata['merchant_name']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['klarna:authorized_payment_method:type'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.payment_method_type')}</Dt>
                            <Dd><code>{transaction.metadata['klarna:authorized_payment_method:type']}</code></Dd>
                        </Dl>
                    )}
                    {transaction.metadata['klarna:authorized_payment_method:number_of_days'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.number_of_days')}</Dt>
                            <Dd><code>{transaction.metadata['klarna:authorized_payment_method:number_of_days']}</code></Dd>
                        </Dl>
                    )}
                    {transaction.metadata['klarna:authorized_payment_method:number_of_installments'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.number_of_installments')}</Dt>
                            <Dd><code>{transaction.metadata['klarna:authorized_payment_method:number_of_installments']}</code></Dd>
                        </Dl>
                    )}
                    {transaction.metadata['klarna:authorized_payment_method:fraud_status'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.klarna.fraud_status')}</Dt>
                            <Dd><code>{transaction.metadata['klarna:authorized_payment_method:fraud_status']}</code></Dd>
                        </Dl>
                    )}
                    {expirationDate && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.authorization_expiration')}</Dt>
                            <Dd><DateTime isoString={expirationDate} /></Dd>
                        </Dl>
                    )}
                </>
            )}

            {transaction.payment_product === 'swish' && transaction.metadata && (
                <>
                    <Spacer />
                    {transaction.metadata['swish:payment_request:id'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.swish.payment_request_id')}</Dt>
                            <Dd>{transaction.metadata['swish:payment_request:id']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['swish:swish_number'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.swish.swish_number')}</Dt>
                            <Dd>{transaction.metadata['swish:swish_number']}</Dd>
                        </Dl>
                    )}
                    {transaction.metadata['swish:paymentReference'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.swish.payment_reference')}</Dt>
                            <Dd>{transaction.metadata['swish:paymentReference']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {(session || (transaction.metadata && (transaction.metadata as any)['payout_correlation_id'])) && (
                <>
                    <Spacer />
                    {session && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.dintero.session_id')}</Dt>
                            <Dd>{session.id} <UnstyledLink to={`/${accountId}/payments/sessions/${session.id}`}>{'\ud83d\udd17'}</UnstyledLink></Dd>
                        </Dl>
                    )}
                    {transaction.metadata && (transaction.metadata as any)['payout_correlation_id'] && (
                        <Dl>
                            <Dt doubleColumn>{t('payments.fields.dintero.payout_correlation_id')}</Dt>
                            <Dd>{(transaction.metadata as any)['payout_correlation_id']}</Dd>
                        </Dl>
                    )}
                </>
            )}

            {((transaction.metadata && (transaction.metadata as any)['payout'])) && (
                <>
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.dintero.payout')}</Dt>
                        <Dd>{t('payments.fields.dintero.payout_type', { context: (transaction.metadata as any)['payout'] })}</Dd>
                    </Dl>
                </>
            )}
        </Wrapper>
    );
};

export default withTranslation()(TransactionData);

const Wrapper = styled.div`
    padding-top: ${distances.normal};
    max-width: ${grid.spans.span6};
    margin-right: ${grid.gutter};
    width: 100%;

    @media (max-width: 1200px) {
        margin-right: 0;
        max-width: 100%;
    }
`;

const Spacer = styled.div`
    height: ${distances.normal};
`;
